import {
  EventAttributionSourceType,
  EventIcon,
  EventSegmentType,
  EventStatus,
  EventType,
  InviteStatus,
} from "@/types/enums";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { BlockType } from "@/types/enums/event";
import {
  EventCreateStatus,
  GoogleCalendarSyncOwner,
} from "@/types/enums/google-calendar-sync";
import { RecurrenceModificationMode } from "@/types/enums/recurrence-modification-mode";
import { RebookingIntervalUnit } from "@/types/forms/question";
import {
  PublicServiceSummary,
  ServiceDetail,
  ServiceTiny,
} from "@/types/service";

import { BookableDetail } from "./bookable";
import { LocationDetail, LocationTiny } from "./location";
import { ProviderTiny } from "./provider";
import { QuickNoteSummary } from "./quick-note";
import { UserTiny } from "./user";

// ============================================================================
// Event Segment Types (Discriminated Union)
// ============================================================================

/** Base properties shared by all segment types */
export interface EventSegmentBase {
  id: string;
  eventId: string;
  start: string;
  end: string;
  externalId?: string | null;
  isArchived?: boolean | null;
  createdDate?: string;
  updatedDate?: string;
}

/** Service segment - represents a service within an event */
export interface EventServiceTiny extends EventSegmentBase {
  type: EventSegmentType.SERVICE;
  service: ServiceTiny;
  providerId?: string | null;
  provider?: ProviderTiny | null;
  serviceAddonId?: string | null;
  /** The service this add-on was booked under. */
  addonParentServiceId?: string | null;
  hasNoShowSmsTemplateConfigured?: boolean;
  hasNoShowSmsSequenceConfigured?: boolean;
  /** The service's delay-0 APPOINTMENT_CANCELLED SMS step template, if any. */
  cancellationSmsStepTemplate?: string | null;
}

/** Service segment with full service details */
export interface EventServiceDetail extends EventSegmentBase {
  type: EventSegmentType.SERVICE;
  service: ServiceDetail;
  providerId?: string | null;
  provider?: ProviderTiny | null;
  serviceAddonId?: string | null;
  /** The service this add-on was booked under. */
  addonParentServiceId?: string | null;
  hasNoShowSmsTemplateConfigured?: boolean;
  hasNoShowSmsSequenceConfigured?: boolean;
  /** The service's delay-0 APPOINTMENT_CANCELLED SMS step template, if any. */
  cancellationSmsStepTemplate?: string | null;
}

/** Bookable segment - represents a bookable resource within an event */
export interface EventBookableSegmentTiny extends EventSegmentBase {
  type: EventSegmentType.BOOKABLE;
  bookableId: string;
  bookable?: BookableDetail | null;
  /** When set, this segment came from a bookable group. ``bookableId`` may be
   * empty if the backend will pick the member at save time. */
  bookableGroupId?: string | null;
}

/** Prep/block time segment - represents prep time before a service */
export interface EventPrepSegmentTiny extends EventSegmentBase {
  type: EventSegmentType.PREP;
  providerId?: string | null;
  provider?: ProviderTiny | null;
  title?: string | null;
  color?: string | null;
}

/** Cleanup time segment - represents cleanup time after services */
export interface EventCleanupSegmentTiny extends EventSegmentBase {
  type: EventSegmentType.CLEANUP;
  providerId?: string | null;
  provider?: ProviderTiny | null;
  title?: string | null;
  color?: string | null;
}

/** Union type for all segment types (tiny/summary version) */
export type EventSegmentTiny =
  | EventServiceTiny
  | EventBookableSegmentTiny
  | EventPrepSegmentTiny
  | EventCleanupSegmentTiny;

/** Union type for all segment types (detail version) */
export type EventSegmentDetail =
  | EventServiceDetail
  | EventBookableSegmentTiny
  | EventPrepSegmentTiny
  | EventCleanupSegmentTiny;

// ============================================================================
// Segment Helper Functions (Type Guards)
// ============================================================================

/** Type guard to check if a segment is a service segment */
export function isServiceSegment(
  segment: EventSegmentCreate,
): segment is EventServiceCreate;
export function isServiceSegment(
  segment: EventSegmentTiny | EventSegmentDetail,
): segment is EventServiceTiny | EventServiceDetail;
export function isServiceSegment(
  segment: EventSegmentCreate | EventSegmentTiny | EventSegmentDetail,
): segment is EventServiceCreate | EventServiceTiny | EventServiceDetail;
export function isServiceSegment(
  segment: EventSegmentCreate | EventSegmentTiny | EventSegmentDetail,
): boolean {
  return segment.type === EventSegmentType.SERVICE;
}

/** Type guard to check if a segment is a bookable segment */
export function isBookableSegment(
  segment: EventSegmentCreate,
): segment is EventBookableSegmentCreate;
export function isBookableSegment(
  segment: EventSegmentTiny | EventSegmentDetail,
): segment is EventBookableSegmentTiny;
export function isBookableSegment(
  segment: EventSegmentCreate | EventSegmentTiny | EventSegmentDetail,
): segment is EventBookableSegmentCreate | EventBookableSegmentTiny {
  return segment.type === EventSegmentType.BOOKABLE;
}

/** Type guard to check if a segment is a prep segment */
export function isPrepSegment(
  segment: EventSegmentTiny | EventSegmentDetail,
): segment is EventPrepSegmentTiny {
  return segment.type === EventSegmentType.PREP;
}

/** Helper to get service segments from an array of segments (overloaded for detail/tiny) */
export function getServiceSegments(
  segments: EventSegmentDetail[],
): EventServiceDetail[];
export function getServiceSegments(
  segments: EventSegmentTiny[],
): EventServiceTiny[];
export function getServiceSegments(
  segments: (EventSegmentTiny | EventSegmentDetail)[],
): (EventServiceTiny | EventServiceDetail)[] {
  return segments.filter(isServiceSegment) as (
    | EventServiceTiny
    | EventServiceDetail
  )[];
}

/** Helper to get the prep segment from an array of segments (returns first one found) */
export function getPrepSegment(
  segments: EventSegmentTiny[],
): EventPrepSegmentTiny | undefined;
export function getPrepSegment(
  segments: EventSegmentDetail[],
): EventPrepSegmentTiny | undefined;
export function getPrepSegment(
  segments: (EventSegmentTiny | EventSegmentDetail)[],
): EventPrepSegmentTiny | undefined {
  return segments.find(isPrepSegment) as EventPrepSegmentTiny | undefined;
}

/** Type guard to check if a segment is a cleanup segment */
export function isCleanupSegment(
  segment: EventSegmentTiny | EventSegmentDetail,
): segment is EventCleanupSegmentTiny {
  return segment.type === EventSegmentType.CLEANUP;
}

/** Helper to get the cleanup segment from an array of segments (returns first one found) */
export function getCleanupSegment(
  segments: EventSegmentTiny[],
): EventCleanupSegmentTiny | undefined;
export function getCleanupSegment(
  segments: EventSegmentDetail[],
): EventCleanupSegmentTiny | undefined;
export function getCleanupSegment(
  segments: (EventSegmentTiny | EventSegmentDetail)[],
): EventCleanupSegmentTiny | undefined {
  return segments.find(isCleanupSegment) as EventCleanupSegmentTiny | undefined;
}

/** Helper to get bookable segments from an array of segments */
export function getBookableSegments(
  segments: EventSegmentTiny[],
): EventBookableSegmentTiny[];
export function getBookableSegments(
  segments: EventSegmentDetail[],
): EventBookableSegmentTiny[];
export function getBookableSegments(
  segments: (EventSegmentTiny | EventSegmentDetail)[],
): EventBookableSegmentTiny[] {
  return segments.filter(isBookableSegment) as EventBookableSegmentTiny[];
}

/** Returns true for provider-free blocks that reserve one or more bookable resources. */
export function isBookableBlockEvent(
  event: Pick<EventTiny, "type" | "attendees" | "segments">,
): boolean {
  return (
    event.type === EventType.BLOCK &&
    event.attendees.length === 0 &&
    getBookableSegments(event.segments || []).length > 0
  );
}

/** Helper to extract service IDs from any segment array (Create, Tiny, or Detail) */
export function getServiceIdsFromSegments(
  segments: (EventSegmentCreate | EventSegmentTiny | EventSegmentDetail)[],
): string[] {
  return segments
    .filter(isServiceSegment)
    .map((seg) => {
      if ("serviceId" in seg) {
        return seg.serviceId;
      }
      if ("service" in seg && seg.service) {
        return seg.service.id;
      }
      return "";
    })
    .filter((id): id is string => Boolean(id));
}

// Types that are referenced but not defined elsewhere
export interface MouseRef {
  current: {
    isMouseDown: boolean;
    startY: number | null;
  } | null;
}

export interface Availability {
  serviceId: string;
  providerId?: string;
  start: Date;
  end: Date;
  locationId?: string;
}

export interface AttendeeDetail {
  user: UserTiny;
  inviteStatus: InviteStatus;
}

export interface CarePlanContext {
  carePlanId: string;
  carePlanName: string;
  stepNumber: number;
  status: string;
}

export interface EventAttributionSummary {
  staffId: string | null;
  sourceType: EventAttributionSourceType | null;
  sourceId: string | null;
  attributedAt: string | null;
}

/** What cancelling or no-showing the appointment would charge right now, in cents. */
export interface AppointmentFees {
  cancellationFee: number;
  noShowFee: number;
}

/**
 * A service segment on a patient's own appointment, as served to an
 * unauthenticated caller. Mirrors `PublicEventService` on the API — no
 * `provider`, which is a `ProviderTiny` carrying staff email and phone.
 */
export interface PublicEventService {
  type: EventSegmentType.SERVICE;
  id: string;
  start: string;
  end: string;
  /** Opaque id, kept so reschedule can pre-select the same clinician. */
  providerId?: string | null;
  /** Marks an add-on segment; reschedule uses it to rebuild the selection. */
  serviceAddonId?: string | null;
  service: PublicServiceSummary;
}

/**
 * A patient's own upcoming appointment, as served to an unauthenticated caller.
 *
 * Mirrors `PublicEventTiny` on the API. Deliberately not `EventTiny`: that
 * carries the clinic's staff on `attendees`, its `quickNotes` (free clinical
 * text plus the author's email), and a provider on every segment.
 */
export interface PublicEventTiny {
  id: string;
  title: string | null;
  start: string;
  end: string;
  status: EventStatus;
  location?: LocationTiny | null;
  /** Service segments only — the API filters the rest out. */
  segments: PublicEventService[];
  /** Quoted by the server so the patient's cancel dialog can name the fee. */
  fees?: AppointmentFees | null;
  /** A telehealth visit, by its location or its services. */
  isVirtual?: boolean;
}

/** A current note linked to an appointment, with what picks it for a service. */
export interface EventNoteRef {
  id: string;
  templateId?: string | null;
  providerId?: string | null;
}

export interface EventTiny {
  id: string;
  title: string | null;
  start: string;
  end: string;
  type: EventType;
  status: EventStatus;
  location: LocationTiny;
  roomId?: string | null;
  attendees: AttendeeDetail[];
  recurringEvent?: RecurringEventResponse;
  eHeight?: number;
  mouseRef?: MouseRef;
  modalPosition?: { left: number; top: number };
  recurringEventId?: string;
  /** Unified segments array */
  segments: EventSegmentTiny[];
  icons: EventIcon[];
  noteIds?: string[];
  /**
   * The same notes as `noteIds`, newest first, with each note's template and
   * author. Only the event detail reads send it, not the calendar list.
   */
  noteRefs?: EventNoteRef[];
  /** True when the patient booked it themselves. Only the calendar list sets it. */
  bookedOnline?: boolean;
  capacity?: number;
  quickNotes?: QuickNoteSummary[];
  carePlan?: CarePlanContext | null;
  createdDate?: string;
  updatedDate?: string;
  meetingLink?: string;
  /**
   * A telehealth visit: its location is virtual, or every main service on it
   * is a virtual service. Read this, not `location.isVirtual`.
   */
  isVirtual?: boolean;
  /**
   * True for Google Calendar all-day events. Stored with start == end so
   * they have zero duration and don't participate in scheduling conflicts;
   * rendered as a pill at the top of the day rather than as a timed block.
   */
  isAllDay?: boolean;
  /**
   * For GOOGLE_CALENDAR events: true when the source calendar marks the
   * event as busy (transparency null or != "transparent"). Mirrors the
   * backend availability check — "free/transparent" gcal events should
   * not trigger overlap warnings. Always undefined for non-gcal events.
   */
  gcalBusy?: boolean;
  description?: string | null;
  patientRequestedProvider?: boolean;
  isBusinessBlock?: boolean;
  blockType?: BlockType | null;
  /** Quoted by the server only on reads that feed a cancel or no-show dialog. */
  fees?: AppointmentFees | null;
}

/**
 * Project a just-booked appointment onto the shape the self-schedule patient
 * payload uses, so an appointment appended locally after booking matches what
 * the API would have returned on the next fetch.
 */
export function toPublicEventTiny(
  event: Pick<
    EventTiny,
    "id" | "title" | "start" | "end" | "status" | "location"
  > & {
    segments: EventSegmentTiny[] | EventSegmentDetail[];
  },
): PublicEventTiny {
  return {
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    status: event.status,
    location: event.location,
    segments: getServiceSegments(event.segments as EventSegmentTiny[]).map(
      (segment) => ({
        type: EventSegmentType.SERVICE as const,
        id: segment.id,
        start: segment.start,
        end: segment.end,
        providerId: segment.providerId,
        serviceAddonId: segment.serviceAddonId,
        service: segment.service,
      }),
    ),
  };
}

export interface GoogleCalendarSyncFailure {
  owner: GoogleCalendarSyncOwner;
  reason: "token_expired" | "calendar_not_enabled";
}

export interface EventCreateResponse {
  events: EventDetail[];
  status: EventCreateStatus;
  googleCalendarFailures: GoogleCalendarSyncFailure[];
  /** Break blocks saved with a shift. */
  breakEvents?: EventDetail[];
}

export interface EventUpdateResponse {
  events: EventDetail[];
  googleCalendarFailures: GoogleCalendarSyncFailure[];
  breakEvents?: EventDetail[];
  removedBreakEventIds?: string[];
}

export interface EventStatusUpdateResponse extends EventDetail {
  event: EventDetail;
  googleCalendarFailures: GoogleCalendarSyncFailure[];
}

export interface EventDeleteResponse {
  eventIds: string[];
  googleCalendarFailures: GoogleCalendarSyncFailure[];
  removedBreakEventIds?: string[];
}

export interface AppointmentMemoInput {
  /** Existing memo id. Omit when creating a memo with the appointment update. */
  id?: string;
  note: string;
}

export interface EventCreate {
  id?: string;
  title: string | null;
  organizerId: string;
  start: Date;
  end: Date;
  locationId?: string;
  locationIds?: string[];
  roomId?: string | null;
  type: EventType;
  attendeeIds?: string[];
  bookableIds?: string[] | null;
  recurringEvent?: RecurringEventCreate;
  confirmationText?: string;
  segments?: EventSegmentCreate[];
  capacity?: number;
  description?: string | null;
  opportunityId?: string;
  patientRequestedProvider?: boolean;
  isBusinessBlock?: boolean;
  /** Waitlist entry fulfilled atomically by this appointment. */
  waitlistId?: string;
  blockType?: BlockType | null;
  /** SHIFT only. Omit to let the location's break rules place breaks; [] saves none. */
  breaks?: ShiftBreakInput[];
  /** Memo text persisted atomically with an appointment create. */
  memos?: string[];
}

export interface EventUpdate {
  id: string;
  title?: string | null;
  organizerId?: string;
  start?: Date;
  end?: Date;
  locationId?: string | null;
  roomId?: string | null;
  type?: EventType;
  attendeeIds?: string[];
  bookableIds?: string[] | null;
  recurringEvent?: RecurringEventCreate;
  recurrenceModificationMode?: RecurrenceModificationMode;
  confirmationText?: string;
  tz?: string;
  segments?: EventSegmentCreate[];
  capacity?: number | null;
  description?: string | null;
  patientRequestedProvider?: boolean;
  isBusinessBlock?: boolean;
  blockType?: BlockType | null;
  /** SHIFT only. The shift's complete break blocks; omit to leave them untouched. */
  breaks?: ShiftBreakInput[];
  /** Complete desired memo state, persisted atomically with the appointment update. */
  memos?: AppointmentMemoInput[];
}

export interface EventDetail extends EventTiny {
  /** The booking-event link this appointment arrived on, when it arrived on one. */
  bookingEvent?: { id: string; name: string } | null;
  organizer: UserTiny;
  /** Override segments with detail version for service segments */
  segments: EventSegmentDetail[];
  recurringEvent?: RecurringEventDetail;
  confirmationText?: string;
  location: LocationDetail | LocationTiny;
  createdDate?: string;
  attribution?: EventAttributionSummary | null;
}

/** A follow-up a provider named on the visit's note. Ids only — the service
 *  and provider lists are already cached client-side, so both names resolve
 *  for free. `serviceId` also prefills the booking form. */
export interface RecommendedFollowUp {
  serviceId: string;
  intervalLabel: string;
  intervalValue: number;
  intervalUnit: RebookingIntervalUnit;
  /** Minutes added to (positive) or taken off (negative) the service's
   *  default length when booking. */
  adjustMinutes?: number | null;
  suggestedById?: string;
}

/** A step that must be completed before the step carrying it can be worked.
 *  `completed` is a server snapshot; prefer the live row when it's in hand. */
export interface AppointmentChecklistDependency {
  id: string;
  name: string;
  completed: boolean;
}

export interface AppointmentChecklistItem {
  id: string; // serviceChecklistId
  eventId: string;
  serviceId?: string;
  name: string;
  type: AppointmentChecklistType | null;
  optional: boolean;
  showInPatientStatus: boolean;
  memoEnabled?: boolean;
  position: number;
  measurementFieldId?: string | null;
  measurementGroupId?: string | null;
  completedDate?: string;
  completedByUser?: UserTiny;
  /** Only on REBOOK steps, and only when the visit's note named follow-ups. */
  recommendedServices?: RecommendedFollowUp[];
  /** Steps on this appointment that must be completed first. */
  dependsOn?: AppointmentChecklistDependency[];
}

export interface PatientStatusEventTiny {
  id: string;
  start: string;
  end: string;
  locationId?: string;
  locationTimezone?: string | null;
  attendees: UserTiny[];
  serviceNames: string[];
  preferredProviderId?: string | null;
}

export interface PatientStatusEventResponse {
  event: PatientStatusEventTiny;
  checklist: AppointmentChecklistItem[];
}

export interface UpdatedAppointment {
  updatedFrom: EventDetail;
  updatedTo: EventDetail;
  patient: UserTiny;
  provider: UserTiny;
}

export interface ProviderWithServices extends UserTiny {
  services: ServiceTiny[];
  visibleLocationIds?: string[];
  isArchived?: boolean;
  bookingCapacity?: number;
}

export interface RecurringEventSegmentTemplate {
  type: EventSegmentType;
  serviceId?: string | null;
  // The add-on mapping a SERVICE segment is an add-on through, so each
  // occurrence keeps it an add-on rather than a main service.
  serviceAddonId?: string | null;
  bookableId?: string | null;
  bookableGroupId?: string | null;
  providerId?: string | null;
  title?: string | null;
  color?: string | null;
  durationMinutes?: number | null;
  bufferMinutes?: number | null;
  offsetMinutes?: number | null;
}

export interface RecurringEventCreate {
  id?: string;
  rrule: string;
  startDate: Date;
  durationMinutes: number;
  timezone: string;
  segments?: RecurringEventSegmentTemplate[];
  bookableIds?: string[] | null;
  capacity?: number | null;
  description?: string | null;
  isBusinessBlock?: boolean;
  blockType?: BlockType | null;
}

export interface ShiftBreakInput {
  start: Date;
  end: Date;
  blockType: BlockType;
}

export interface RecurringEventUpdate extends RecurringEventCreate {
  id: string;
  type: EventType;
  title: string;
  attendeeIds?: string[];
  locationId?: string;
}

export type RecurringEventResponse = Omit<RecurringEventCreate, "startDate"> & {
  startDate: string;
};

/** A saved recurrence as form state: the rule's start parsed back into a `Date`. */
export const toRecurringEventCreate = (
  recurringEvent: RecurringEventResponse,
): RecurringEventCreate => ({
  ...recurringEvent,
  startDate: new Date(recurringEvent.startDate),
});

export type RecurringEventDetail = Omit<RecurringEventUpdate, "startDate"> & {
  startDate: string;
  organizer: UserTiny;
};

export interface PositionedEvent {
  column: number;
  totalColumns: number;
  isInShift: boolean;
}

export interface ShiftRow {
  provider: UserTiny;
  shifts: EventTiny[];
  blocks: EventTiny[][];
  allDayEvents: EventTiny[];
}

export interface DayShift {
  day: Date;
  shifts: ShiftRow[];
}

export interface ProviderShiftsData {
  provider: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  shifts: EventTiny[];
  appointments: EventTiny[];
}

export interface ShiftsByDateResponse {
  date: string;
  providers: ProviderShiftsData[];
}

// ============================================================================
// Segment Create Types
// ============================================================================

export interface EventServiceCreate {
  type: EventSegmentType.SERVICE;
  serviceId: string;
  providerId?: string | null;
  start?: Date | null;
  end?: Date | null;
  externalId?: string | null;
  isArchived?: boolean;
  serviceAddonId?: string | null;
}

export interface EventBookableSegmentCreate {
  type: EventSegmentType.BOOKABLE;
  bookableId: string;
  /** When the bookable was picked from a bookable group, records which group
   * so the backend can re-pick on reschedule and so concurrent group-pick
   * logic can avoid double-reservations. */
  bookableGroupId?: string | null;
  start?: Date | null;
  end?: Date | null;
}

export interface EventPrepSegmentCreate {
  type: EventSegmentType.PREP;
  /** Nullable — the backend will auto-assign a prep provider when not set. */
  providerId: string | null;
  start?: Date | null;
  end?: Date | null;
  title?: string | null;
  color?: string | null;
}

export interface EventCleanupSegmentCreate {
  type: EventSegmentType.CLEANUP;
  providerId?: string | null;
  start?: Date | null;
  end?: Date | null;
  title?: string | null;
  color?: string | null;
}

/** Prep time config type for form state */
export interface PrepTimeFormConfig {
  id: string;
  start: Date;
  end: Date;
  title?: string | null;
  color?: string | null;
  providerId?: string | null;
}

export type EventSegmentCreate =
  | EventServiceCreate
  | EventBookableSegmentCreate
  | EventPrepSegmentCreate
  | EventCleanupSegmentCreate;

export interface TimeDiff {
  fromStart?: string | null;
  toStart?: string | null;
}

export interface LocationDiff {
  fromLocation?: LocationTiny | null;
  toLocation?: LocationTiny | null;
}

export interface RoomDiff {
  fromRoomName?: string | null;
  toRoomName?: string | null;
}

export interface StaffDiff {
  added: UserTiny[];
  removed: UserTiny[];
  unchanged?: UserTiny[];
}

export interface ServiceEndpoint {
  start?: string | null;
  end?: string | null;
  provider?: ProviderTiny | null;
}

export interface ServiceDiff {
  serviceId: string;
  serviceTitle?: string | null;
  from?: ServiceEndpoint | null;
  to?: ServiceEndpoint | null;
  changeTypes: ServiceChangeType[];
}

export type UpdatedAppointmentChangedField =
  | "time"
  | "location"
  | "room"
  | "staff"
  | "services"
  | "status";

export type ServiceChangeType =
  | "added"
  | "removed"
  | "provider_changed"
  | "time_changed";

export interface UpdatedAppointment {
  updatedFrom: EventDetail;
  updatedTo: EventDetail;
  patient: UserTiny;
  provider: UserTiny;
  staffDiff?: StaffDiff | null;
  serviceDiffs: ServiceDiff[];
  timeDiff?: TimeDiff | null;
  locationDiff?: LocationDiff | null;
  roomDiff?: RoomDiff | null;
  changedFields: UpdatedAppointmentChangedField[];
}
